// Lightweight persistence for Onboarding document templates.
// PDF bytes are stored as a data URL in localStorage (fine for typical contract
// PDFs of a few hundred KB). Field placements + roles are stored alongside.
// Swap for a Supabase table/storage bucket when a backend is desired.

export type FieldType = 'signature' | 'radio' | 'checkbox' | 'text' | 'date' | 'select';

export interface PlacedField {
  id: string;
  type: FieldType;
  roleId: string;
  page: number;
  /** normalized 0..1 coordinates relative to the rendered page */
  x: number;
  y: number;
  w: number;
  h: number;
  label?: string;
  required?: boolean;
  /** select / radio */
  options?: string[];
  /** checkbox */
  checked?: boolean;
  /** date */
  useSigningDate?: boolean;
  dateFormat?: 'MDY' | 'DMY' | 'YMD';
  /** text + date */
  autoPopulate?: string;      // key of a value to auto-populate from
  defaultText?: string;       // "Or enter text"
  /** text */
  format?: 'any' | 'email' | 'number' | 'phone';
}

export interface DocRole {
  id: string;
  name: string;
  color: string;
}

export interface DocTemplate {
  id: string;
  name: string;
  description: string;
  pdfDataUrl: string | null;
  roles: DocRole[];
  fields: PlacedField[];
  createdAt: string;
  updatedAt: string;
}

const KEY = 'onboarding:docTemplates';

function readAll(): Record<string, DocTemplate> {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
}
function writeAll(all: Record<string, DocTemplate>) {
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch (e) { console.error('Failed to save templates (storage full?)', e); }
}

export const onboardingDocsStore = {
  list(): DocTemplate[] {
    return Object.values(readAll()).sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  },
  get(id: string): DocTemplate | null {
    return readAll()[id] ?? null;
  },
  save(t: DocTemplate) {
    const all = readAll();
    all[t.id] = { ...t, updatedAt: new Date().toISOString() };
    writeAll(all);
  },
  remove(id: string) {
    const all = readAll();
    delete all[id];
    writeAll(all);
  },
};

export const uid = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);
